import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getUserDeviceAccessAPI } from '../services/serverService';

const DeviceAccessDetailsPage = () => {
    const { serverId, role, userId } = useParams();
    
    const [devices, setDevices] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchDevices = async () => {
            try {
                setIsLoading(true);
                const response = await getUserDeviceAccessAPI(serverId, role, userId);
                setDevices(response.data.data || []);
            } catch (error) {
                console.error("Failed to fetch device access", error);
                alert("Failed to load device details.");
            } finally {
                setIsLoading(false);
            }
        };

        if (serverId && role && userId) fetchDevices();
    }, [serverId, role, userId]);

    const formatDate = (dateString) => {
        if (!dateString) return 'Unknown';
        return new Date(dateString).toLocaleString('en-GB', { 
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
        });
    };

    return (
        <div className="page-container" style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div className="page-header" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '20px' }}>
                <Link to={`/servers/${serverId}/login-history`} style={{ textDecoration: 'none', color: '#718096', fontSize: '13px', fontWeight: '500' }}>
                    ← Back to Login History
                </Link>
                <div style={{ marginTop: '15px' }}>
                    <h1 style={{ margin: 0, color: '#2c3e50', fontSize: '24px' }}>Device Access Details</h1>
                    <p style={{ margin: '5px 0 0 0', color: '#718096', fontSize: '14px' }}>
                        Viewing known devices for User ID: <strong>{userId}</strong> ({role})
                    </p>
                </div>
            </div>

            <div style={{ marginTop: '20px' }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>Loading device data...</div>
                ) : devices.length > 0 ? (
                    devices.map((device) => (
                        <div key={device.id} style={{ 
                            backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', 
                            border: '1px solid #e2e8f0', marginBottom: '20px', overflow: 'hidden',
                            display: 'flex', flexDirection: 'column'
                        }}>
                            {/* Card Header */}
                            <div style={{ 
                                backgroundColor: device.current ? '#ebf8ff' : '#f7fafc', 
                                borderBottom: '1px solid #e2e8f0', padding: '15px 20px',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '20px' }}>
                                        {device.deviceType.toLowerCase().includes('browser') || device.name.toLowerCase().includes('website') ? '💻' : '📱'}
                                    </span>
                                    <h3 style={{ margin: 0, color: '#2d3748', fontSize: '16px' }}>{device.name}</h3>
                                    {device.current && (
                                        <span style={{ backgroundColor: '#3182ce', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                            LATEST SESSION
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: '13px', color: '#718096' }}>
                                    Access Allowed: <strong style={{ color: device.access ? '#38a169' : '#e53e3e' }}>{device.access ? 'Yes' : 'No'}</strong>
                                </div>
                            </div>

                            {/* Card Body */}
                            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                {/* Data Section */}
                                <div style={{ flex: '1', minWidth: '300px', padding: '20px' }}>
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#a0aec0', fontWeight: 'bold', textTransform: 'uppercase' }}>Device Model</label>
                                        <div style={{ color: '#2d3748', fontWeight: '500', marginTop: '4px' }}>{device.model}</div>
                                    </div>
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#a0aec0', fontWeight: 'bold', textTransform: 'uppercase' }}>Last Login Timestamp</label>
                                        <div style={{ color: '#2d3748', fontWeight: '500', marginTop: '4px' }}>{formatDate(device.lastLogin)}</div>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#a0aec0', fontWeight: 'bold', textTransform: 'uppercase' }}>Raw Coordinates</label>
                                        <div style={{ color: '#718096', fontSize: '13px', marginTop: '4px', fontFamily: 'monospace' }}>
                                            {device.location ? `${device.location.lat.toFixed(6)}, ${device.location.lng.toFixed(6)}` : 'Location not captured'}
                                        </div>
                                    </div>
                                </div>

                                {/* Map Section */}
                                <div style={{ flex: '1', minWidth: '300px', backgroundColor: '#edf2f7', borderLeft: '1px solid #e2e8f0' }}>
                                    {device.location ? (
                                        <iframe 
                                            title={`map-${device.id}`}
                                            width="100%" 
                                            height="100%" 
                                            style={{ border: 0, minHeight: '200px' }} 
                                            src={`https://maps.google.com/maps?q=${device.location.lat},${device.location.lng}&z=14&output=embed`} 
                                            allowFullScreen 
                                        />
                                    ) : (
                                        <div style={{ height: '100%', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a0aec0', padding: '20px', textAlign: 'center' }}>
                                            No map data available for this session.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', color: '#718096' }}>
                        No device history found for this user.
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeviceAccessDetailsPage;